import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { examApi, type ExamContent, type Question } from '../../services/examApi';
import { RichHtml } from '../../components/exam/RichHtml';
import { QuestionView } from '../../components/exam/QuestionView';
import { DesmosCalculator } from '../../components/exam/DesmosCalculator';

/** 拉平后的一个模块步骤：section 名 + template(计时/计算器/指导语) + 题目 */
interface ModuleStep {
  sectionName: string;
  subject: string;
  moduleNo: number;
  timeLimitSec: number | null;
  calculator: 'allowed' | 'not_allowed' | null;
  directionsHtml: string | null;
  questions: Question[];
}

function buildSteps(data: ExamContent): ModuleStep[] {
  const steps: ModuleStep[] = [];
  const tSections = data.template?.sections || [];
  const cSections = data.content?.sections || [];
  tSections.forEach((ts, si) => {
    // 按索引对齐 template/content 的 section（迁移工具保证两者同序同数量）。
    // 不用 subject 做键：AP 一卷多个 section 可能共享同一 subject，用 subject 会全部塌缩到第一个。
    const cs = cSections[si] ?? cSections.find((c) => c.subject === ts.subject);
    ts.modules.forEach((tm) => {
      const cm = cs?.modules.find((m) => m.moduleNo === tm.moduleNo);
      steps.push({
        sectionName: ts.name,
        subject: ts.subject,
        moduleNo: tm.moduleNo,
        timeLimitSec: tm.timeLimitSec,
        calculator: tm.calculator,
        directionsHtml: tm.directions?.html ?? null,
        questions: cm?.questions || [],
      });
    });
  });
  return steps;
}

function formatTime(sec: number): string {
  const neg = sec < 0;
  const s = Math.abs(sec);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${neg ? '-' : ''}${mm}:${ss}`;
}

type Phase = 'directions' | 'question' | 'done';

const ExamRunnerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ExamContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>('directions');
  const [moduleIndex, setModuleIndex] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 各模块用时（秒），key: subject|moduleNo
  const durationsRef = useRef<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCalc, setShowCalc] = useState(false);
  const [calcMounted, setCalcMounted] = useState(false); // 首次打开后常驻挂载，避免重复创建实例

  const steps = useMemo(() => (data ? buildSteps(data) : []), [data]);
  const step = steps[moduleIndex];

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    examApi
      .getContent(id)
      .then((res) => { if (mounted) { setData(res); setLoading(false); } })
      .catch((e: any) => { if (mounted) { setError(e.message || 'Failed to load'); setLoading(false); } });
    return () => { mounted = false; };
  }, [id]);

  // 计时：仅在答题阶段运行
  useEffect(() => {
    if (phase !== 'question' || !step) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase, moduleIndex]);

  const startModule = () => {
    setRemaining(step?.timeLimitSec ?? 0);
    setQIndex(0);
    setPhase('question');
  };

  const answerKey = (mi: number, sort: number) => `m${mi}_${sort}`;

  const currentQuestion = step?.questions[qIndex];
  const setAnswer = (value: any) => {
    if (!currentQuestion) return;
    setAnswers((a) => ({ ...a, [answerKey(moduleIndex, currentQuestion.sort)]: value }));
  };

  const goPrev = () => setQIndex((i) => Math.max(0, i - 1));
  const goNext = () => setQIndex((i) => Math.min((step?.questions.length ?? 1) - 1, i + 1));

  /** 记录当前模块用时 = 限时 - 剩余（无限时则记 0） */
  const recordDuration = (s: typeof step) => {
    if (!s) return;
    const spent = s.timeLimitSec != null ? Math.max(0, s.timeLimitSec - remaining) : 0;
    durationsRef.current[`${s.subject}|${s.moduleNo}`] = spent;
  };

  /** 把 answers 状态拆成客观题作答项 + FRQ 作答，用于交卷 */
  const buildSubmitPayload = () => {
    const objective: { subject: string; moduleNo: number; sort: number; value: any }[] = [];
    const frqResponses: Record<string, any> = {};
    steps.forEach((s, mi) => {
      s.questions.forEach((q) => {
        const val = answers[answerKey(mi, q.sort)];
        const isFrq = q.type === 'response' && (q.parts?.length ?? 0) > 0;
        if (isFrq) {
          if (val != null) frqResponses[`${s.subject}|${s.moduleNo}|${q.sort}`] = val;
        } else {
          objective.push({ subject: s.subject, moduleNo: s.moduleNo, sort: q.sort, value: val ?? null });
        }
      });
    });
    return {
      answers: objective,
      frqResponses,
      partDurations: durationsRef.current,
    };
  };

  const finalizeSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await examApi.submitAttempt(id, buildSubmitPayload());
      navigate(`/report/${result.attemptId}`);
    } catch (e: any) {
      setSubmitError(e?.message || 'Failed to submit');
      setSubmitting(false);
    }
  };

  const submitModule = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    recordDuration(step);
    if (moduleIndex < steps.length - 1) {
      setModuleIndex((m) => m + 1);
      setPhase('directions');
    } else {
      setPhase('done');
      void finalizeSubmit();
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center text-gray-400">Loading exam…</div>;
  if (error) return (
    <div className="h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-red-500">{error}</p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>Back to list</button>
    </div>
  );
  if (!data || steps.length === 0) return <div className="h-screen flex items-center justify-center text-gray-400">No content.</div>;

  // 完成页
  if (phase === 'done') {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-5 bg-primary-50/30">
        {submitError ? (
          <>
            <div className="text-5xl">⚠️</div>
            <h2 className="text-2xl font-semibold text-gray-800">Submission failed</h2>
            <p className="text-red-500">{submitError}</p>
            <div className="flex gap-3">
              <button className="btn btn-primary" onClick={finalizeSubmit}>Retry</button>
              <button className="btn" onClick={() => navigate('/')}>Back to list</button>
            </div>
          </>
        ) : submitting ? (
          <>
            <div className="text-5xl animate-pulse">⏳</div>
            <h2 className="text-2xl font-semibold text-gray-800">Scoring your exam…</h2>
            <p className="text-gray-500">Generating your report.</p>
          </>
        ) : (
          <>
            <div className="text-5xl">✓</div>
            <h2 className="text-2xl font-semibold text-gray-800">Exam submitted</h2>
            <button className="btn btn-primary" onClick={() => navigate('/')}>Back to list</button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-primary-50/30">
      {/* Header */}
      <header className="h-14 shrink-0 flex items-center justify-between px-6 bg-white border-b border-primary-100">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-600 text-sm">✕ Exit</button>
          <h2 className="font-semibold text-gray-800 truncate">{data.exam.title}</h2>
          <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
            {step.sectionName} · Module {step.moduleNo}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {step.calculator === 'allowed' && (
            <button
              onClick={() => { setCalcMounted(true); setShowCalc((v) => !v); }}
              className={`text-xs px-3 py-1 rounded-full transition ${showCalc ? 'bg-primary-500 text-white' : 'bg-primary-100 text-primary-700 hover:bg-primary-200'}`}
            >
              🖩 Calculator
            </button>
          )}
          {step.calculator === 'not_allowed' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">🚫 No calculator</span>
          )}
          {phase === 'question' && (
            <>
              <span className={`font-mono text-lg ${remaining < 0 ? 'text-red-500' : 'text-gray-700'}`}>{formatTime(remaining)}</span>
              <span className="text-sm text-gray-400">{qIndex + 1} / {step.questions.length}</span>
            </>
          )}
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 overflow-y-auto">
        {phase === 'directions' ? (
          <div className="max-w-2xl mx-auto p-8">
            <div className="card p-8 space-y-5">
              <h3 className="text-xl font-semibold text-gray-800">
                {step.sectionName} · Module {step.moduleNo} — Directions
              </h3>
              {step.directionsHtml ? (
                <RichHtml html={step.directionsHtml} className="prose max-w-none text-gray-700" />
              ) : (
                <p className="text-gray-500">This module has {step.questions.length} questions.</p>
              )}
              <div className="text-sm text-gray-500">
                {step.timeLimitSec ? `Time limit: ${Math.round(step.timeLimitSec / 60)} minutes` : 'No time limit'}
                {step.calculator === 'allowed' && ' · Calculator allowed'}
                {step.calculator === 'not_allowed' && ' · Calculator not allowed'}
              </div>
              <button className="btn btn-primary" onClick={startModule}>Start</button>
            </div>
          </div>
        ) : (
          currentQuestion && (
            <div className="max-w-3xl mx-auto p-6">
              <div className="card p-6">
                <div className="text-xs text-gray-400 mb-3">Question {currentQuestion.displayNo ?? currentQuestion.sort}</div>
                <QuestionView
                  question={currentQuestion}
                  answer={answers[answerKey(moduleIndex, currentQuestion.sort)]}
                  onChange={setAnswer}
                />
              </div>
            </div>
          )
        )}
      </main>

      {/* NavBar */}
      {phase === 'question' && (
        <footer className="h-16 shrink-0 flex items-center justify-between px-6 bg-white border-t border-primary-100">
          <button onClick={goPrev} disabled={qIndex === 0} className="btn btn-primary disabled:opacity-40">◀ Back</button>

          <div className="flex items-center gap-1.5 flex-wrap max-w-[50%] justify-center">
            {step.questions.map((q, i) => {
              const answered = answers[answerKey(moduleIndex, q.sort)] != null && answers[answerKey(moduleIndex, q.sort)] !== '';
              return (
                <button
                  key={q.sort}
                  onClick={() => setQIndex(i)}
                  className={`w-6 h-6 rounded-full text-[11px] transition ${
                    i === qIndex ? 'bg-primary-500 text-white' : answered ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          {qIndex < step.questions.length - 1 ? (
            <button onClick={goNext} className="btn btn-primary">Next ▶</button>
          ) : (
            <button onClick={submitModule} className="btn btn-primary">
              {moduleIndex < steps.length - 1 ? 'Submit Module ▶' : 'Submit Exam ✓'}
            </button>
          )}
        </footer>
      )}

      {calcMounted && (
        <DesmosCalculator
          visible={showCalc && step?.calculator === 'allowed'}
          onClose={() => setShowCalc(false)}
        />
      )}
    </div>
  );
};

export default ExamRunnerPage;
